import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Progress,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  BanIcon,
  CheckCircle2Icon,
  ClockIcon,
  CoinsIcon,
  InfoIcon,
  PackageIcon,
  SaveIcon,
  ShieldCheckIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import { useRef, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import {
  useShopQuery,
  useSuspendShop,
  useUnsuspendShop,
  useUpdateShop,
} from "../api/queries";
import { ShopFeatureFlagsPanel } from "../ui/ShopFeatureFlagsPanel";
import type { ShopFormValues } from "../ui/ShopProfileForm";
import { ShopProfileForm } from "../ui/ShopProfileForm";
import { ShopStaffTable } from "../ui/ShopStaffTable";

function shopCode(id: string): string {
  const short = id.replace(/-/g, "").slice(-8).toUpperCase();
  return `SH-${short}`;
}

const STATUS_LABELS: Record<string, string> = {
  active: "ໃຊ້ງານປົກກະຕິ",
  suspended: "ລະງັບ",
  unverified: "ລໍຖ້າຢືນຢັນ",
};

const VERIFIED_LABELS: Record<string, string> = {
  none: "ຍັງບໍ່ຢືນຢັນ",
  basic: "ພື້ນຖານ",
  verified: "ຢືນຢັນແລ້ວ",
};

function profileCompletion(shop: {
  name?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  taxId?: string | null;
  defaultTaxPercent?: number;
}): number {
  let filled = 0;
  const total = 6;
  if (shop.name?.trim()) filled++;
  if (shop.phone?.trim()) filled++;
  if (shop.address?.trim()) filled++;
  if (shop.city?.trim()) filled++;
  if (shop.taxId?.trim()) filled++;
  if ((shop.defaultTaxPercent ?? 0) > 0) filled++;
  return Math.round((filled / total) * 100);
}

export function ShopDetailPage() {
  const nav = useNavigate({ from: "/app/shops/$id" });
  const { id } = useParams({ from: "/app/shops/$id" });
  const formRef = useRef<{ submit: () => void } | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const { data: shop, ...result } = useShopQuery(id ?? "");
  const updateShop = useUpdateShop(id ?? "");
  const suspendShop = useSuspendShop(id ?? "");
  const unsuspendShop = useUnsuspendShop(id ?? "");

  const canUpdate = useActionPermission(["shops:update"]);
  const canAddStaff = canUpdate; // ໃຊ້ shops:update ເພີ່ມ staff
  const canSuspend = useActionPermission(["shops:suspend"]);
  const canVerify = useActionPermission(["shops:verify"]);
  const canFeeRules = useActionPermission(["fee_rules:list"]);
  const canReadCatalog = useActionPermission(["catalog:read"]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຮ້ານຄ້າ", to: "/app/shops" },
            { label: shop?.name ?? "ລາຍລະອຽດ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ລາຍລະອຽດຮ້ານຄ້າ</h2>
            <p className="text-muted-foreground">
              {shop ? `${shop.name} • ${shopCode(shop.id)}` : "ກຳລັງໂຫຼດ..."}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {canSuspend &&
              shop &&
              (shop.status === "suspended" ? (
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      await unsuspendShop.mutateAsync();
                      toast.success("ຍົກເລີກລະງັບຮ້ານສຳເລັດ");
                    } catch {
                      toast.error("ບໍ່ສາມາດຍົກເລີກລະງັບໄດ້");
                    }
                  }}
                  isLoading={unsuspendShop.isPending}
                >
                  <BanIcon className="size-4" />
                  ຍົກເລີກລະງັບ
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="border-destructive text-destructive hover:bg-destructive"
                  onClick={async () => {
                    try {
                      await suspendShop.mutateAsync();
                      toast.success("ລະງັບຮ້ານສຳເລັດ");
                    } catch {
                      toast.error("ບໍ່ສາມາດລະງັບໄດ້");
                    }
                  }}
                  disabled={
                    suspendShop.isPending || shop.status === "unverified"
                  }
                >
                  <BanIcon className="size-4" /> ລະງັບຮ້ານ
                </Button>
              ))}
            {canVerify && shop && (
              <Button
                onClick={() =>
                  nav({ to: "/app/shops/$id/verify", params: { id: shop.id } })
                }
                variant="outline"
              >
                <ShieldCheckIcon className="size-4" />
                ຢືນຢັນຮ້ານ
              </Button>
            )}
            {canFeeRules && shop && (
              <Button
                variant="outline"
                onClick={() =>
                  nav({
                    to: "/app/fees",
                    search: { tab: "by_shop", shopId: shop.id },
                  })
                }
              >
                <CoinsIcon className="size-4" />
                ຕັ້ງຄ່າຄ່າທຳນຽມ
              </Button>
            )}
            {canReadCatalog && shop && (
              <Button
                variant="outline"
                onClick={() =>
                  nav({
                    to: "/app/shops/$id/products",
                    params: { id: shop.id },
                  })
                }
              >
                <PackageIcon className="size-4" />
                ສິນຄ້າຂອງຮ້ານ
              </Button>
            )}
            {canUpdate && (
              <Button
                className="bg-[#F26522] hover:bg-[#F26522]/90"
                onClick={() => formRef.current?.submit()}
                disabled={updateShop.isPending}
              >
                <SaveIcon className="size-4" />
                ບັນທຶກການປ່ຽນແປງ
              </Button>
            )}
            {/* <Button variant="outline" onClick={() => nav({ to: "/app/shops" })}>
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button> */}
          </div>
        </div>

        {showSuccess && (
          <Alert className="mt-4 border-green-500/50 bg-green-500/10">
            <CheckCircle2Icon className="size-4 text-green-600" />
            <AlertDescription>ບັນທຶກການປ່ຽນແປງຂອງຮ້ານຄ້າສຳເລັດແລ້ວ</AlertDescription>
            <button
              type="button"
              onClick={() => setShowSuccess(false)}
              className="absolute top-2 right-2 rounded p-1 hover:bg-black/10"
              aria-label="ປິດ"
            >
              <XIcon className="size-4" />
            </button>
          </Alert>
        )}

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດຮ້ານຄ້າ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!shop ? null : (
            <div className="mt-6 grid gap-6 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <InfoIcon className="size-5 text-[#F26522]" />
                      ຂໍ້ມູນທົ່ວໄປ
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ShopProfileForm
                      ref={formRef}
                      initialValues={{
                        name: shop.name,
                        phone: shop.phone ?? "",
                        address: shop.address ?? "",
                        city: shop.city ?? "",
                        taxId: shop.taxId ?? "",
                        defaultTaxPercent: shop.defaultTaxPercent ?? 0,
                        defaultCurrency: (shop.defaultCurrency ??
                          "LAK") as ShopFormValues["defaultCurrency"],
                        enforceSingleShopSkuCurrency:
                          shop.enforceSingleShopSkuCurrency ?? false,
                      }}
                      onSubmit={async (vals: ShopFormValues) => {
                        try {
                          await updateShop.mutateAsync({
                            name: vals.name,
                            phone: vals.phone || null,
                            address: vals.address || null,
                            city: vals.city || null,
                            taxId: vals.taxId?.trim() || null,
                            defaultTaxPercent: vals.defaultTaxPercent,
                            defaultCurrency: vals.defaultCurrency,
                            enforceSingleShopSkuCurrency:
                              vals.enforceSingleShopSkuCurrency,
                          });
                          setShowSuccess(true);
                          setTimeout(() => setShowSuccess(false), 5000);
                        } catch {
                          toast.error("ບັນທຶກລົ້ມເຫຼວ");
                        }
                      }}
                      submitting={updateShop.isPending}
                    />
                  </CardContent>
                </Card>

                <ShopFeatureFlagsPanel
                  shopId={shop.id}
                  flags={shop.featureFlags}
                  canUpdate={canUpdate}
                />

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <UsersIcon className="size-5 text-[#004799]" />
                      ພະນັກງານ
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ShopStaffTable
                      staff={shop.staff}
                      shopId={shop.id}
                      canAddStaff={canAddStaff}
                    />
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="flex size-8 items-center justify-center rounded bg-[#F26522]/20">
                        <CheckCircle2Icon className="size-5 text-[#F26522]" />
                      </span>
                      ສະຖານະທຸລະກິດ
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div
                      className={
                        shop.status === "active"
                          ? "rounded-r-md border-green-500 border-l-4 bg-muted/50 p-3"
                          : shop.status === "suspended"
                            ? "rounded-r-md border-red-500 border-l-4 bg-muted/50 p-3"
                            : "rounded-r-md border-muted-foreground/50 border-l-4 bg-muted/50 p-3"
                      }
                    >
                      <div className="text-muted-foreground text-xs uppercase tracking-wider">
                        ສະຖານະປັດຈຸບັນ
                      </div>
                      <div className="mt-1 flex items-center justify-between">
                        <span
                          className={
                            shop.status === "active"
                              ? "font-bold text-green-600 text-lg dark:text-green-500"
                              : shop.status === "suspended"
                                ? "font-bold text-destructive text-lg"
                                : "font-bold text-lg text-muted-foreground"
                          }
                        >
                          {STATUS_LABELS[shop.status] ?? shop.status}
                        </span>
                        {shop.status === "active" && (
                          <CheckCircle2Icon className="size-5 shrink-0 text-green-600 dark:text-green-500" />
                        )}
                      </div>
                    </div>

                    <div className="rounded-r-md border-[#F26522] border-l-4 bg-muted/50 p-3">
                      <div className="text-muted-foreground text-xs uppercase tracking-wider">
                        ລະດັບການຢືນຢັນ
                      </div>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="font-bold text-[#F26522] text-lg">
                          {shop.verifiedLevel === "verified"
                            ? "LEVEL 2 - ຢືນຢັນແລ້ວ"
                            : shop.verifiedLevel === "basic"
                              ? "LEVEL 1 - ພື້ນຖານ"
                              : (VERIFIED_LABELS[
                                  shop.verifiedLevel ?? "none"
                                ] ?? shop.verifiedLevel)}
                        </span>
                        <ShieldCheckIcon className="size-5 shrink-0 text-[#F26522]" />
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-muted-foreground text-xs uppercase tracking-wider">
                          ຄວາມສົມບູນແບບ
                        </span>
                        <span className="font-medium">
                          {profileCompletion(shop)}%
                        </span>
                      </div>
                      <Progress
                        value={profileCompletion(shop)}
                        className="h-2 [&>div]:bg-[#F26522]"
                      />
                    </div>
                  </CardContent>
                </Card>

                <SummaryCard
                  variant="grid"
                  title="ປະຫວັດ Metadata"
                  icon={ClockIcon}
                  items={[
                    {
                      label: "ສ້າງເມື່ອ",
                      value: formatDateTimeLocal(shop.createdAt),
                    },
                    {
                      label: "ແກ້ໄຂຄັ້ງຫຼ້າ",
                      value: formatDateTimeLocal(shop.updatedAt),
                    },
                  ]}
                />
              </div>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
